import TokenLogo from '@/src/components/AppComponents/TokenLogoWithCircle/TokenLogoWithCircle';
import { useTokensStoreForFunds } from '@/src/hooks/useTokensStoreForFunds';
import {
    FundWithDetailsEntity,
    PriceHistoricApi,
    Token_With_Change24,
    Token_With_Price_And_Date_And_Signature_And_Amount_And_Metadata_And_Percentage,
} from '@/src/lib/MayzSmartDB';
import { showToken_Price_MultipliedByAmount_Rounded, showToken_Price_Rounded } from '@/src/store/global/tokensStoreModelHelpers';
import { useAppStore } from '@/src/store/global/useGlobalStore';
import { hexToStr } from '@/src/lib/MayzSmartDB';
import { formatPercentage } from '@/src/lib/SmartDB/Commons/formatters';
import { INVESTUNIT_DECIMALS, INVESTUNIT_MULTIPLIER } from '@/src/utils/constants';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import styles from './TokenDetails.module.scss';

interface TokenDetailsProps {
    fund: FundWithDetailsEntity;
    isHide?: boolean;
    isTablet: boolean;
    isDesktop?: boolean;
}

export default function TokenDetails(props: TokenDetailsProps) {
    const { fund, isHide, isTablet, isDesktop } = props;
    //--------------------------------------
    const appStore = useAppStore();
    //--------------------------------------
    const { tokensStore, isTokensStoreReady, tokensFromStore } = useTokensStoreForFunds({
        mode: 'FundDetails',
        name: 'FundDetailsForTokensDetails',
        isLoadedDetails: true,
        fund: fund,
        followUp: undefined,
        keepAlive: undefined,
        addTokenFT: true,
        addSubTokensFT: true,
        swAddPrice: true,
        swAddMetadata: true,
        initPriceUpdater: false,
    });
    //--------------------------------------
    const [tokensMetadataAndAmountAndPercentage, setTokensMetadataAndAmountAndPercentage] = useState<
        Token_With_Price_And_Date_And_Signature_And_Amount_And_Metadata_And_Percentage[]
    >([]);
    const [tokensChange24, setTokensChange24] = useState<Token_With_Change24[]>([]);
    //--------------------------------------
    useEffect(() => {
        if (isTokensStoreReady === true) {
            //--------------------------------------
            const tokensWithMetadataAndAmount = fund.investUnit!.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.map((tokenInvestUnit) => {
                const tokensPriceAndMetadata = tokensStore.getTokenPriceAndMetadata(tokenInvestUnit.CS, tokenInvestUnit.TN_Hex);
                const tokenWithAmount = { ...tokenInvestUnit, ...tokensPriceAndMetadata, amount: tokenInvestUnit?.amount ?? 0n, percentage: tokenInvestUnit?.percentage ?? 0n };
                return tokenWithAmount as Token_With_Price_And_Date_And_Signature_And_Amount_And_Metadata_And_Percentage;
            });
            //--------------------------------------
            setTokensMetadataAndAmountAndPercentage([...tokensWithMetadataAndAmount]);
            //--------------------------------------
            const fetchChange24 = async () => {
                const updatedTokens: Token_With_Change24[] = [];
                for (const token of tokensWithMetadataAndAmount) {
                    const tokenChange24 = await PriceHistoricApi.get_Token_Change_24_Percent_Api(token.CS, token.TN_Hex, token.priceADAx1e6, false);
                    updatedTokens.push({
                        ...token,
                        change24: tokenChange24,
                    });
                    setTokensChange24([...updatedTokens]); // Updates state after each token
                }
            };
            fetchChange24();
        }
    }, [isTokensStoreReady]);
    //--------------------------------------
    if (isHide && isTablet && !isDesktop) {
        return null;
    }
    //--------------------------------------
    return (
        <div className={`${styles.tokenDetailContainer} ${!isTablet && !isHide && !isDesktop ? styles.expanded : ''} `}>
            <div className={styles.tokensGroup}>
                {tokensMetadataAndAmountAndPercentage.map((token) => (
                    <div className={styles.token} key={hexToStr(token.TN_Hex)}>
                        <TokenLogo token={token} />
                        <div className={styles.tokenInfo}>
                            <div className={styles.tokenData}>
                                <div className={styles.dataSmall}>
                                    <NextLink
                                        href={appStore.siteSettings !== undefined ? `${appStore.siteSettings.gettaptools_url_explorer_mainnet(token.CS, token.TN_Hex)}` : `#`}
                                        legacyBehavior
                                    >
                                        <a className={styles.tokenLink} target="_blank" rel="noopener noreferrer">
                                            {token.ticker}
                                        </a>
                                    </NextLink>
                                </div>
                                <p className={styles.dataSmall}>{formatPercentage(token.percentage, 0)}</p>
                            </div>
                            <div className={styles.tokenData}>
                                <div className={styles.data}>
                                    <div className={styles.textGray}>{`${Number(token.amount) / INVESTUNIT_MULTIPLIER} x ${showToken_Price_Rounded(
                                        tokensStore,
                                        token.CS,
                                        token.TN_Hex
                                    )}`}</div>
                                </div>
                                <div className={styles.data}>
                                    {`${showToken_Price_MultipliedByAmount_Rounded(
                                        tokensStore,
                                        fund.tokenFT?.decimals === undefined ? undefined : token.amount * BigInt(10 ** fund.tokenFT!.decimals!),
                                        token.CS,
                                        token.TN_Hex,
                                        INVESTUNIT_DECIMALS
                                    )}`}
                                    {(() => {
                                        const tokenChange = tokensChange24.find((t) => t.CS === token.CS && t.TN_Hex === token.TN_Hex)?.change24;
                                        return (
                                            <>
                                                <div
                                                    className={`${styles.change24} ${
                                                        tokenChange !== undefined ? (tokenChange < 0 ? styles.negative : styles.positive) : styles.positive
                                                    }`}
                                                >
                                                    {tokenChange !== undefined ? `(${formatPercentage(tokenChange, 2)})` : null}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
